import { memo, useRef, useState } from 'react';
import { CloseIcon, Button } from '@hst-horizon/horizon-ui';
import { clsx } from 'clsx';
import { useIntl } from 'react-intl';
import { useDebouncedState } from '@react-hookz/web';
import SearchDropdown from '../../../layout/components/Header/components/SearchField/SearchDropdown';
import TextInput from '../../../core/components/input/TextInput';
import SearchIcon from '../../../core/components/icons/SearchIcon';
import useCustomNavigate from '../../../core/hooks/router/useCustomNavigate';
import useSubmitSearchData from '../../hooks/useSubmitSearchData';
import useNewProductSearch from '../../queries/useNewProductSearch';
import useSuggestBrands from '../../queries/useSuggestBrands';
import useSuggestCategories from '../../queries/useSuggestCategories';
import useTrackSearchTerm from '../../../main/hooks/googleAnalytics/useTrackSearchTerm';
import styles from './SearchField.module.scss';

const SearchFieldDropdown = () => {
    const intl = useIntl();
    const navigate = useCustomNavigate();

    const [value, setValue] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebouncedState(
        '',
        250
    );

    const [isFocused, setIsFocused] = useState(false);
    const isOpen = Boolean(value.length >= 2 && isFocused);

    const {
        brands = [],
        loading: brandsLoading,
        error: brandsError,
    } = useSuggestBrands({
        search: debouncedSearchTerm,
        skip: debouncedSearchTerm.length < 2,
    });

    const {
        categories = [],
        loading: categoriesLoading,
        error: categoriesError,
    } = useSuggestCategories({
        search: debouncedSearchTerm,
        skip: debouncedSearchTerm.length < 2,
    });

    const {
        products,
        loading,
        error: productsError,
    } = useNewProductSearch({
        search: debouncedSearchTerm,
        skip: debouncedSearchTerm.length < 2,
    });

    useTrackSearchTerm(debouncedSearchTerm, 'header');

    const genericLoading = value !== debouncedSearchTerm;

    const searchInputRef = useRef<HTMLInputElement | null>(null);

    const [targetUrl] = useSubmitSearchData({
        brands: brands,
        categories: categories,
        products,
        query: encodeURIComponent(value),
        loading:
            loading || brandsLoading || categoriesLoading || genericLoading,
    });

    const resetSearch = () => {
        setDebouncedSearchTerm('');
        setValue('');
    };

    const submitSearch = () => {
        navigate(targetUrl);
        resetSearch();
        if (searchInputRef.current) {
            searchInputRef.current.blur();
        }
    };

    const initiateSearch = () => {
        if (value !== '') {
            submitSearch();
        }
    };

    const clearSearch = () => {
        resetSearch();
    };

    const handleChange = (searchInput: string) => {
        setValue(searchInput);
        setDebouncedSearchTerm(searchInput);
    };

    return (
        <SearchDropdown
            isOpen={isOpen}
            searchValue={value}
            onOpenChange={setIsFocused}
            categories={{
                items: categories,
                loading: categoriesLoading,
                error: categoriesError,
            }}
            brands={{
                items: brands,
                error: brandsError,
                loading: brandsLoading,
            }}
            products={{
                items: products,
                loading: loading,
                error: productsError,
            }}
            resetSearch={resetSearch}
            submitSearch={submitSearch}
        >
            <div className={clsx(styles.search, styles.searchList)}>
                <input style={{ display: 'none' }} autoComplete="off" />
                <TextInput
                    hasStartAdornment
                    hasEndAdornment
                    greyBackground
                    noGreyBorder
                    material={false}
                    ref={searchInputRef}
                    aria-label="search"
                    data-test-class="searchPreviewField"
                    id="search"
                    value={value}
                    onChange={e => handleChange(e.target.value)}
                    placeholder={intl.formatMessage({
                        id: 'find products',
                    })}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => {
                        setTimeout(() => {
                            setIsFocused(false);
                        }, 150);
                    }}
                    autoComplete="globalSearch"
                    startAdornment={
                        <Button text inline onClick={initiateSearch}>
                            <SearchIcon />
                        </Button>
                    }
                    endAdornment={
                        value.length > 1 && (
                            <Button text inline onClick={clearSearch}>
                                <CloseIcon />
                            </Button>
                        )
                    }
                    onKeyPress={(
                        event: React.KeyboardEvent<HTMLInputElement>
                    ) => {
                        if (event.key === 'Enter') {
                            initiateSearch();
                        }
                    }}
                />
            </div>
        </SearchDropdown>
    );
};

export default memo(SearchFieldDropdown);
